import type {
  StorageActorSlice,
  StorageAnalyticsResponse,
  StorageContributor,
  StorageInfoShareOwner,
  StorageModuleSlice,
  StorageSedrRoomItem,
  StorageSharedFolderItem,
} from '../api/client';
import { t } from '../i18n';
import { formatBytes as formatStorageBytes } from '../i18n/formatBytes';
import { downloadTextFile } from './adminAudit';
import { enhanceDataTableCards } from '../ui/dataTableCards';

export { formatStorageBytes };

export const ACTOR_PIE_TOP_N = 8;

const PIE_COLORS = [
  '#0f6e56',
  '#2f6fed',
  '#b45309',
  '#7c3aed',
  '#be123c',
  '#0e7490',
  '#4d7c0f',
  '#9333ea',
  '#c2410c',
  '#475569',
];


export function moduleLabel(module: string): string {
  const key = `admin.disk.module.${module}`;
  const translated = t(key);
  return translated === key ? module : translated;
}

export type PieSlice = {
  key: string;
  label: string;
  bytes: number;
  color: string;
};

export function buildModulePieSlices(modules: StorageModuleSlice[]): PieSlice[] {
  return modules
    .filter((slice) => slice.bytes > 0)
    .map((slice, index) => ({
      key: slice.module,
      label: moduleLabel(slice.module),
      bytes: slice.bytes,
      color: PIE_COLORS[index % PIE_COLORS.length]!,
    }));
}

export function buildActorPieSlices(actors: StorageActorSlice[], topN = ACTOR_PIE_TOP_N): PieSlice[] {
  const positive = actors.filter((actor) => actor.bytes > 0);
  if (positive.length === 0) {
    return [];
  }

  const top = positive.slice(0, topN);
  const rest = positive.slice(topN);
  const slices: PieSlice[] = top.map((actor, index) => ({
    key: actorKey(actor),
    label: actor.displayName,
    bytes: actor.bytes,
    color: PIE_COLORS[index % PIE_COLORS.length]!,
  }));

  if (rest.length > 0) {
    slices.push({
      key: 'other-actors',
      label: t('admin.disk.actors.other'),
      bytes: rest.reduce((sum, actor) => sum + actor.bytes, 0),
      color: PIE_COLORS[topN % PIE_COLORS.length]!,
    });
  }

  return slices;
}

function actorKey(actor: StorageActorSlice | StorageContributor): string {
  if (actor.userId) {
    return `user:${actor.userId}`;
  }
  if (actor.guestSessionId) {
    return `guest:${actor.guestSessionId}`;
  }
  return `unknown:${actor.displayName}`;
}

export function renderPieChart(
  slices: PieSlice[],
  title: string,
  options?: { showLegend?: boolean; compact?: boolean },
): HTMLElement {
  const showLegend = options?.showLegend !== false;
  const compact = options?.compact === true;
  const wrap = document.createElement('div');
  wrap.className = compact ? 'disk-pie disk-pie--compact' : 'disk-pie';

  if (title) {
    const heading = document.createElement('h3');
    heading.className = 'disk-pie__title';
    heading.textContent = title;
    wrap.append(heading);
  }

  const total = slices.reduce((sum, slice) => sum + slice.bytes, 0);
  if (total <= 0 || slices.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.textContent = t('admin.disk.empty');
    wrap.append(empty);
    return wrap;
  }

  const body = document.createElement('div');
  body.className = 'disk-pie__body';

  const size = compact ? 140 : 180;
  const radius = compact ? 62 : 80;
  const cx = size / 2;
  const cy = size / 2;
  let angle = -Math.PI / 2;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', title || t('admin.disk.charts.byActor'));
  svg.classList.add('disk-pie__svg');

  for (const slice of slices) {
    const portion = slice.bytes / total;
    const sweep = portion * Math.PI * 2;
    const startX = cx + radius * Math.cos(angle);
    const startY = cy + radius * Math.sin(angle);
    angle += sweep;
    const endX = cx + radius * Math.cos(angle);
    const endY = cy + radius * Math.sin(angle);
    const largeArc = sweep > Math.PI ? 1 : 0;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    if (slices.length === 1 || portion >= 0.9999) {
      path.setAttribute(
        'd',
        `M ${cx} ${cy} m ${-radius} 0 a ${radius} ${radius} 0 1 1 ${radius * 2} 0 a ${radius} ${radius} 0 1 1 ${-radius * 2} 0`,
      );
    } else {
      path.setAttribute(
        'd',
        `M ${cx} ${cy} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`,
      );
    }
    path.setAttribute('fill', slice.color);
    const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    titleEl.textContent = `${slice.label}: ${formatStorageBytes(slice.bytes)} (${(portion * 100).toFixed(1)}%)`;
    path.append(titleEl);
    svg.append(path);
  }

  body.append(svg);

  if (showLegend) {
    const legend = document.createElement('ul');
    legend.className = 'disk-pie__legend';
    for (const slice of slices) {
      const portion = slice.bytes / total;
      const item = document.createElement('li');
      item.className = 'disk-pie__legend-item';
      item.innerHTML = `
        <span class="disk-pie__swatch" style="background:${slice.color}"></span>
        <span class="disk-pie__legend-label">${escapeHtml(slice.label)}</span>
        <span class="disk-pie__legend-meta">${escapeHtml(formatStorageBytes(slice.bytes))} · ${(portion * 100).toFixed(1)}%</span>
      `;
      legend.append(item);
    }
    body.append(legend);
  }

  wrap.append(body);
  return wrap;
}

export function renderAnalyticsCharts(analytics: StorageAnalyticsResponse): HTMLElement {
  const row = document.createElement('div');
  row.className = 'disk-analytics-charts';
  row.append(
    renderPieChart(buildModulePieSlices(analytics.modules), t('admin.disk.charts.byModule')),
    renderPieChart(buildActorPieSlices(analytics.actors), t('admin.disk.charts.byActor')),
  );
  return row;
}

export function createCsvButton(labelKey: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button ghost';
  button.setAttribute('data-i18n', labelKey);
  button.textContent = t(labelKey);
  button.addEventListener('click', onClick);
  return button;
}

export function renderStorageTableSection(options: {
  titleKey: string;
  exportLabelKey?: string;
  onExport?: () => void;
  table: HTMLElement;
}): HTMLElement {
  const section = document.createElement('div');
  section.className = 'disk-analytics-table-section';

  const header = document.createElement('div');
  header.className = 'disk-analytics-table-section__header';
  const title = document.createElement('h3');
  title.setAttribute('data-i18n', options.titleKey);
  title.textContent = t(options.titleKey);
  header.append(title);
  if (options.onExport) {
    header.append(createCsvButton(options.exportLabelKey ?? 'admin.disk.csv.export', options.onExport));
  }
  section.append(header, options.table);
  return section;
}

export function renderSharedFoldersTable(
  items: StorageSharedFolderItem[],
  expandedId: string | null,
  contributors: StorageContributor[] | null,
  onToggle: (folderId: string) => void,
): HTMLElement {
  if (items.length === 0) {
    return emptyNote('admin.disk.empty');
  }

  const table = document.createElement('table');
  table.className = 'data-table disk-analytics-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th data-i18n="admin.disk.table.owner"></th>
        <th data-i18n="admin.disk.table.shortId"></th>
        <th data-i18n="admin.disk.table.created"></th>
        <th data-i18n="admin.disk.table.bytes"></th>
        <th></th>
      </tr>
    </thead>
  `;
  const body = document.createElement('tbody');
  for (const item of items) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${escapeHtml(item.ownerDisplayName)}</td>
      <td></td>
      <td>${escapeHtml(formatDate(item.createdAt))}</td>
      <td>${escapeHtml(formatStorageBytes(item.totalBytes))}</td>
    `;
    const idCell = row.children[1] as HTMLTableCellElement;
    idCell.append(renderCopyableId(item.folderId, item.shortId));
    const actionCell = document.createElement('td');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button ghost';
    const open = expandedId === item.folderId;
    button.textContent = t(open ? 'admin.disk.drilldown.hide' : 'admin.disk.drilldown.show');
    button.addEventListener('click', () => onToggle(item.folderId));
    actionCell.append(button);
    row.append(actionCell);
    body.append(row);

    if (open) {
      const detail = document.createElement('tr');
      detail.className = 'disk-analytics-table__detail';
      const cell = document.createElement('td');
      cell.colSpan = 5;
      cell.append(renderContributorsDrilldown(contributors));
      detail.append(cell);
      body.append(detail);
    }
  }
  table.append(body);
  enhanceDataTableCards(table);
  return table;
}

export function renderSedrRoomsTable(
  items: StorageSedrRoomItem[],
  expandedId: string | null,
  contributors: StorageContributor[] | null,
  onToggle: (roomId: string) => void,
): HTMLElement {
  if (items.length === 0) {
    return emptyNote('admin.disk.empty');
  }

  const table = document.createElement('table');
  table.className = 'data-table disk-analytics-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th data-i18n="admin.disk.table.title"></th>
        <th data-i18n="admin.disk.table.owner"></th>
        <th data-i18n="admin.disk.table.resourceId"></th>
        <th data-i18n="admin.disk.table.created"></th>
        <th data-i18n="admin.disk.table.bytes"></th>
        <th></th>
      </tr>
    </thead>
  `;
  const body = document.createElement('tbody');
  for (const item of items) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${escapeHtml(item.title)}</td>
      <td>${escapeHtml(item.ownerDisplayName)}</td>
      <td></td>
      <td>${escapeHtml(formatDate(item.createdAt))}</td>
      <td>${escapeHtml(formatStorageBytes(item.totalBytes))}</td>
    `;
    const idCell = row.children[2] as HTMLTableCellElement;
    idCell.append(renderCopyableId(item.roomId));
    const actionCell = document.createElement('td');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button ghost';
    const open = expandedId === item.roomId;
    button.textContent = t(open ? 'admin.disk.drilldown.hide' : 'admin.disk.drilldown.show');
    button.addEventListener('click', () => onToggle(item.roomId));
    actionCell.append(button);
    row.append(actionCell);
    body.append(row);

    if (open) {
      const detail = document.createElement('tr');
      detail.className = 'disk-analytics-table__detail';
      const cell = document.createElement('td');
      cell.colSpan = 6;
      cell.append(renderContributorsDrilldown(contributors));
      detail.append(cell);
      body.append(detail);
    }
  }
  table.append(body);
  enhanceDataTableCards(table);
  return table;
}

export function renderInfoShareOwnersTable(
  items: StorageInfoShareOwner[],
  expandedId: string | null,
  onToggle: (ownerUserId: string) => void,
): HTMLElement {
  if (items.length === 0) {
    return emptyNote('admin.disk.empty');
  }

  const table = document.createElement('table');
  table.className = 'data-table disk-analytics-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th data-i18n="admin.disk.table.owner"></th>
        <th data-i18n="admin.disk.table.bytes"></th>
        <th></th>
      </tr>
    </thead>
  `;
  const body = document.createElement('tbody');
  for (const item of items) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${escapeHtml(item.ownerDisplayName)}</td>
      <td>${escapeHtml(formatStorageBytes(item.totalBytes))}</td>
    `;
    const actionCell = document.createElement('td');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button ghost';
    const open = expandedId === item.ownerUserId;
    button.textContent = t(open ? 'admin.disk.drilldown.hide' : 'admin.disk.drilldown.show');
    button.addEventListener('click', () => onToggle(item.ownerUserId));
    actionCell.append(button);
    row.append(actionCell);
    body.append(row);

    if (open) {
      const detail = document.createElement('tr');
      detail.className = 'disk-analytics-table__detail';
      const cell = document.createElement('td');
      cell.colSpan = 3;
      cell.append(renderInfoShareBreakdown(item));
      detail.append(cell);
      body.append(detail);
    }
  }
  table.append(body);
  enhanceDataTableCards(table);
  return table;
}

function buildContributorPieSlices(contributors: StorageContributor[]): PieSlice[] {
  return buildActorPieSlices(
    contributors.map((contributor) => ({
      actorKind: contributor.actorKind,
      userId: contributor.userId,
      guestSessionId: contributor.guestSessionId,
      displayName: contributor.displayName,
      bytes: contributor.bytes,
    })),
  );
}

function renderContributorsDrilldown(contributors: StorageContributor[] | null): HTMLElement {
  if (!contributors) {
    const loading = document.createElement('p');
    loading.className = 'muted';
    loading.textContent = t('status.loading');
    return loading;
  }

  if (contributors.length === 0) {
    return emptyNote('admin.disk.empty');
  }

  const slices = buildContributorPieSlices(contributors);
  return renderPieAndTable(slices, t('admin.disk.drilldown.contributors'));
}

function renderInfoShareBreakdown(item: StorageInfoShareOwner): HTMLElement {
  const slices: PieSlice[] = [
    {
      key: 'message',
      label: t('admin.disk.infoShare.messageBytes'),
      bytes: item.messageBytes,
      color: PIE_COLORS[0]!,
    },
    {
      key: 'reply',
      label: t('admin.disk.infoShare.replyBytes'),
      bytes: item.replyBytes,
      color: PIE_COLORS[1]!,
    },
  ].filter((slice) => slice.bytes > 0);

  if (slices.length === 0) {
    return emptyNote('admin.disk.empty');
  }

  return renderPieAndTable(slices, t('admin.disk.drilldown.breakdown'));
}

function renderPieAndTable(slices: PieSlice[], pieTitle: string): HTMLElement {
  const layout = document.createElement('div');
  layout.className = 'disk-analytics-drilldown';

  const pie = renderPieChart(slices, pieTitle, { showLegend: false, compact: true });
  pie.classList.add('disk-analytics-drilldown__pie');

  const table = document.createElement('table');
  table.className = 'data-table disk-analytics-table disk-analytics-table--compact';
  const total = slices.reduce((sum, slice) => sum + slice.bytes, 0);
  table.innerHTML = `
    <thead>
      <tr>
        <th></th>
        <th data-i18n="admin.disk.table.actor">${escapeHtml(t('admin.disk.table.actor'))}</th>
        <th data-i18n="admin.disk.table.bytes">${escapeHtml(t('admin.disk.table.bytes'))}</th>
        <th data-i18n="admin.disk.table.percent">${escapeHtml(t('admin.disk.table.percent'))}</th>
      </tr>
    </thead>
  `;
  const body = document.createElement('tbody');
  for (const slice of slices) {
    const portion = total > 0 ? (slice.bytes / total) * 100 : 0;
    const row = document.createElement('tr');
    row.innerHTML = `
      <td><span class="disk-pie__swatch" style="background:${slice.color}"></span></td>
      <td>${escapeHtml(slice.label)}</td>
      <td>${escapeHtml(formatStorageBytes(slice.bytes))}</td>
      <td>${portion.toFixed(1)}%</td>
    `;
    body.append(row);
  }
  table.append(body);
  enhanceDataTableCards(table);

  layout.append(pie, table);
  return layout;
}

function renderCopyableId(fullId: string, shortLabel?: string): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'disk-analytics-id';
  const code = document.createElement('code');
  code.textContent = shortLabel && shortLabel.length > 0 ? shortLabel : fullId.slice(0, 8);
  code.title = fullId;
  const copy = document.createElement('button');
  copy.type = 'button';
  copy.className = 'button ghost disk-analytics-id__copy';
  copy.textContent = t('admin.disk.copyId');
  copy.title = fullId;
  copy.setAttribute('aria-label', t('admin.disk.copyId'));
  copy.addEventListener('click', () => {
    void navigator.clipboard.writeText(fullId).catch(() => {
      /* ignore clipboard failures */
    });
  });
  wrap.append(code, ' ', copy);
  return wrap;
}

function emptyNote(key: string): HTMLElement {
  const empty = document.createElement('p');
  empty.className = 'muted';
  empty.setAttribute('data-i18n', key);
  empty.textContent = t(key);
  return empty;
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

function toCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

function csvStamp(now = new Date()): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function downloadModulesCsv(modules: StorageModuleSlice[]): void {
  const csv = toCsv(
    [t('admin.disk.csv.module'), t('admin.disk.csv.bytes')],
    modules.map((slice) => [moduleLabel(slice.module), String(slice.bytes)]),
  );
  downloadTextFile(`disk-modules-${csvStamp()}.csv`, csv, 'text/csv;charset=utf-8');
}

export function downloadActorsCsv(actors: StorageActorSlice[]): void {
  const csv = toCsv(
    [
      t('admin.disk.csv.actor'),
      t('admin.disk.csv.actorKind'),
      t('admin.disk.csv.userId'),
      t('admin.disk.csv.guestSessionId'),
      t('admin.disk.csv.bytes'),
    ],
    actors.map((actor) => [
      actor.displayName,
      actor.actorKind,
      actor.userId ?? '',
      actor.guestSessionId ?? '',
      String(actor.bytes),
    ]),
  );
  downloadTextFile(`disk-actors-${csvStamp()}.csv`, csv, 'text/csv;charset=utf-8');
}

export function downloadSharedFoldersCsv(items: StorageSharedFolderItem[]): void {
  const csv = toCsv(
    [
      t('admin.disk.csv.folderId'),
      t('admin.disk.csv.shortId'),
      t('admin.disk.csv.owner'),
      t('admin.disk.csv.ownerUserId'),
      t('admin.disk.csv.created'),
      t('admin.disk.csv.bytes'),
    ],
    items.map((item) => [
      item.folderId,
      item.shortId,
      item.ownerDisplayName,
      item.ownerUserId,
      item.createdAt,
      String(item.totalBytes),
    ]),
  );
  downloadTextFile(`disk-shared-folders-${csvStamp()}.csv`, csv, 'text/csv;charset=utf-8');
}

export function downloadSedrRoomsCsv(items: StorageSedrRoomItem[]): void {
  const csv = toCsv(
    [
      t('admin.disk.csv.roomId'),
      t('admin.disk.csv.title'),
      t('admin.disk.csv.owner'),
      t('admin.disk.csv.ownerUserId'),
      t('admin.disk.csv.created'),
      t('admin.disk.csv.bytes'),
    ],
    items.map((item) => [
      item.roomId,
      item.title,
      item.ownerDisplayName,
      item.ownerUserId,
      item.createdAt,
      String(item.totalBytes),
    ]),
  );
  downloadTextFile(`disk-sedr-rooms-${csvStamp()}.csv`, csv, 'text/csv;charset=utf-8');
}

export function downloadInfoShareOwnersCsv(items: StorageInfoShareOwner[]): void {
  const csv = toCsv(
    [
      t('admin.disk.csv.owner'),
      t('admin.disk.csv.ownerUserId'),
      t('admin.disk.csv.messageBytes'),
      t('admin.disk.csv.replyBytes'),
      t('admin.disk.csv.bytes'),
    ],
    items.map((item) => [
      item.ownerDisplayName,
      item.ownerUserId,
      String(item.messageBytes),
      String(item.replyBytes),
      String(item.totalBytes),
    ]),
  );
  downloadTextFile(`disk-info-share-owners-${csvStamp()}.csv`, csv, 'text/csv;charset=utf-8');
}

export function downloadContributorsCsv(
  resourceLabel: string,
  resourceId: string,
  contributors: StorageContributor[],
): void {
  const csv = toCsv(
    [
      t('admin.disk.csv.actor'),
      t('admin.disk.csv.actorKind'),
      t('admin.disk.csv.userId'),
      t('admin.disk.csv.guestSessionId'),
      t('admin.disk.csv.bytes'),
    ],
    contributors.map((item) => [
      item.displayName,
      item.actorKind,
      item.userId ?? '',
      item.guestSessionId ?? '',
      String(item.bytes),
    ]),
  );
  downloadTextFile(
    `disk-${resourceLabel}-${resourceId.slice(0, 8)}-contributors-${csvStamp()}.csv`,
    csv,
    'text/csv;charset=utf-8',
  );
}
